/**
 * The dashboard's date range: Jalali presets in, a UTC interval out.
 *
 * WHY THIS IS ITS OWN MODULE. «این ماه» must mean the same Jalali month
 * everywhere — the toolbar, a shared link, the comparison period — and three
 * screens each computing it from `new Date()` is three chances for one of them
 * to use a Gregorian month and be wrong by up to eleven days.
 *
 * The conversion uses `Intl.DateTimeFormat` with the `persian` calendar, which
 * the platform already ships and gets right, rather than a second
 * implementation of the arithmetic the services carry. That is the same
 * division of labour as everywhere else: storage and comparison are UTC,
 * display and selection are Jalali, and this module is the boundary.
 */
import { PERSIAN_MONTHS } from './format.js';

const JALALI = new Intl.DateTimeFormat('en-u-ca-persian-nu-latn', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  timeZone: 'UTC',
});

/** {jy, jm, jd} for a JS Date, read in UTC. */
export function toJalaliParts(date) {
  const parts = Object.fromEntries(
    JALALI.formatToParts(date).map((p) => [p.type, p.value]),
  );
  return {
    jy: Number(parts.year),
    jm: Number(parts.month),
    jd: Number(parts.day),
  };
}

/**
 * The UTC Date on which a given Jalali y/m/d begins.
 *
 * Found by SEARCH rather than by arithmetic: `Intl` will convert one way, and
 * inverting it correctly needs the leap rule the services implement. Binary
 * search over a ±400-day window around a linear estimate is exact, costs about
 * ten iterations, and cannot drift from the platform's own calendar — which is
 * the property that matters, because the axis labels come from `Intl` too.
 */
export function fromJalali(jy, jm, jd = 1) {
  // 1 Farvardin 1349 is 21 March 1970. Anchoring there makes the estimate
  // close enough that the search is a handful of steps.
  const estimate = Date.UTC(1970, 2, 21) + Math.round((jy - 1349) * 365.2422 + (jm - 1) * 30.4 + (jd - 1)) * 86400000;
  let low = estimate - 400 * 86400000;
  let high = estimate + 400 * 86400000;
  const target = jy * 10000 + jm * 100 + jd;
  while (low <= high) {
    const mid = low + Math.floor((high - low) / 2 / 86400000) * 86400000;
    const { jy: y, jm: m, jd: d } = toJalaliParts(new Date(mid));
    const value = y * 10000 + m * 100 + d;
    if (value === target) return new Date(mid);
    if (value < target) low = mid + 86400000;
    else high = mid - 86400000;
  }
  return new Date(estimate);
}

export function addJalaliMonths(date, months) {
  const { jy, jm, jd } = toJalaliParts(date);
  const total = jy * 12 + (jm - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  // Clamp the day: Esfand 30 exists only in a leap year, and a cohort table
  // walks forward in months on every render.
  let day = jd;
  for (; day > 28; day -= 1) {
    const candidate = fromJalali(ny, nm, day);
    const parts = toJalaliParts(candidate);
    if (parts.jm === nm && parts.jd === day) break;
  }
  return fromJalali(ny, nm, day);
}

export const PRESETS = ['this_month', 'this_quarter', 'this_year', 'last_30'];

/**
 * A preset -> `{ dateFrom, dateTo }` as ISO strings, half-open [from, to).
 *
 * A QUARTER IS FARVARDIN–KHORDAD, not January–March. A quarter that started in
 * January would cut Nowruz — the single most important boundary in an Iranian
 * business year — in half.
 */
export function resolvePreset(preset, now = new Date()) {
  const { jy, jm } = toJalaliParts(now);
  let from;
  let to;
  switch (preset) {
    case 'this_month':
      from = fromJalali(jy, jm, 1);
      to = addJalaliMonths(from, 1);
      break;
    case 'this_quarter': {
      const qStart = Math.floor((jm - 1) / 3) * 3 + 1;
      from = fromJalali(jy, qStart, 1);
      to = addJalaliMonths(from, 3);
      break;
    }
    case 'this_year':
      from = fromJalali(jy, 1, 1);
      to = addJalaliMonths(from, 12);
      break;
    case 'last_30':
      to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
      from = new Date(to.getTime() - 30 * 86400000);
      break;
    default:
      throw new Error(`unknown preset ${preset}`);
  }
  return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
}

/** «شهریور ۱۴۰۵» for a range, or «۱۴۰۵/۰۶/۰۱ – ۱۴۰۵/۰۶/۳۰» for a custom one. */
export function describeRange({ dateFrom, dateTo }, preset) {
  if (!dateFrom || !dateTo) return '';
  const a = toJalaliParts(new Date(dateFrom));
  // The END is exclusive, so the label must describe the last day INSIDE it —
  // «تا ۱ مهر» for a month ending at Mehr 1 would name a day the data excludes.
  const b = toJalaliParts(new Date(new Date(dateTo).getTime() - 86400000));
  if (preset === 'this_month') return `${PERSIAN_MONTHS[a.jm - 1]} ${a.jy}`;
  if (preset === 'this_quarter') {
    return `فصل ${Math.floor((a.jm - 1) / 3) + 1} ${a.jy}`;
  }
  if (preset === 'this_year') return `سال ${a.jy}`;
  const fmt = (p) => `${p.jy}/${String(p.jm).padStart(2, '0')}/${String(p.jd).padStart(2, '0')}`;
  return `${fmt(a)} – ${fmt(b)}`;
}

/** Shift a resolved range by one whole period, for the `[` and `]` shortcuts. */
export function shiftRange({ dateFrom, dateTo }, preset, direction) {
  if (preset === 'last_30' || !PRESETS.includes(preset)) {
    const span = new Date(dateTo) - new Date(dateFrom);
    return {
      dateFrom: new Date(new Date(dateFrom).getTime() + direction * span).toISOString(),
      dateTo: new Date(new Date(dateTo).getTime() + direction * span).toISOString(),
    };
  }
  const months = { this_month: 1, this_quarter: 3, this_year: 12 }[preset];
  const from = addJalaliMonths(new Date(dateFrom), direction * months);
  return {
    dateFrom: from.toISOString(),
    dateTo: addJalaliMonths(from, months).toISOString(),
  };
}
